const MS_PER_DAY = 24 * 60 * 60 * 1000

interface ParsedDate {
  year: number
  month: number
  day: number
}

/**
 * Parses a yyyy-mm-dd string into numeric parts without going through
 * `new Date(string)`, which avoids UTC/local timezone shift bugs.
 */
const parseISODate = (dateStr: string): ParsedDate => {
  const [year, month, day] = dateStr.split('-').map(Number)
  return { year, month, day }
}

const formatMMDDYYYY = ({ year, month, day }: ParsedDate): string =>
  `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`

// Compares by UTC day-count so month/year boundaries don't break the diff.
const isNextCalendarDay = (previous: ParsedDate, current: ParsedDate): boolean => {
  const previousUTC = Date.UTC(previous.year, previous.month - 1, previous.day)
  const currentUTC = Date.UTC(current.year, current.month - 1, current.day)
  return (currentUTC - previousUTC) / MS_PER_DAY === 1
}

/**
 * Transforms an array of yyyy-mm-dd date strings into a human-readable
 * summary, collapsing runs of consecutive days into "MM/DD/YYYY - MM/DD/YYYY"
 * ranges and joining separate runs/single dates with ", ".
 * @param dates - Array of date strings in yyyy-mm-dd format.
 * @returns The formatted date range summary, or an empty string if `dates` is empty.
 */
export const formatDateSeries = (dates: string[]): string => {
  if (dates.length === 0) return ''

  const sortedUniqueDates = Array.from(new Set(dates)).sort((a, b) =>
    a.localeCompare(b)
  )
  const parsedDates = sortedUniqueDates.map(parseISODate)

  const ranges: string[] = []
  let runStart = parsedDates[0]
  let runEnd = parsedDates[0]

  for (let i = 1; i < parsedDates.length; i++) {
    const current = parsedDates[i]
    if (isNextCalendarDay(runEnd, current)) {
      runEnd = current
    } else {
      ranges.push(formatRange(runStart, runEnd))
      runStart = current
      runEnd = current
    }
  }
  ranges.push(formatRange(runStart, runEnd))

  return ranges.join(', ')
}

const formatRange = (start: ParsedDate, end: ParsedDate): string =>
  start.year === end.year && start.month === end.month && start.day === end.day
    ? formatMMDDYYYY(start)
    : `${formatMMDDYYYY(start)} - ${formatMMDDYYYY(end)}`
